import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", organization: "", password: "" });
  const [tos, setTos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!tos) {
      setError("È necessario accettare i Termini di Servizio e la clausola di Legittimo Interesse.");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, tos_accepted: true });
      toast.success("Account creato");
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6 avz-grid-bg text-left">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-zinc-200 rounded-sm shadow-sm p-8" data-testid="register-form">
        <div className="flex items-center gap-2 mb-6">
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Certy Trace" className="w-9 h-9 object-contain" />
          <span className="font-heading font-black text-lg">Certy Trace</span>
        </div>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">Crea il tuo account</h2>
        <p className="text-sm text-zinc-500 mt-1 mb-6">Piattaforma di due diligence professionale.</p>

        {error && (
          <div data-testid="register-error" className="mb-4 border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase font-mono tracking-wide text-zinc-600">Nome e cognome</Label>
            <Input data-testid="register-name" value={form.name} onChange={set("name")} required className="mt-1.5 border-zinc-300 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase font-mono tracking-wide text-zinc-600">Organizzazione</Label>
            <Input data-testid="register-org" value={form.organization} onChange={set("organization")} className="mt-1.5 border-zinc-300 rounded-sm" placeholder="Studio legale / Azienda" />
          </div>
          <div>
            <Label className="text-xs uppercase font-mono tracking-wide text-zinc-600">Email</Label>
            <Input data-testid="register-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5 border-zinc-300 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase font-mono tracking-wide text-zinc-600">Password (min. 8 caratteri)</Label>
            <Input data-testid="register-password" type="password" value={form.password} onChange={set("password")} required className="mt-1.5 border-zinc-300 rounded-sm" />
          </div>
        </div>

        <label className="flex items-start gap-3 mt-5 cursor-pointer">
          <Checkbox checked={tos} onCheckedChange={setTos} data-testid="register-tos" className="mt-0.5" />
          <span className="text-xs text-zinc-600 leading-relaxed">
            Accetto i <span className="text-zinc-900 underline">Termini di Servizio</span> e dichiaro di operare sotto{" "}
            <strong className="text-zinc-900">Legittimo Interesse (Art. 6.1.f GDPR)</strong>, esclusivamente per finalità
            professionali documentate e verificabili.
          </span>
        </label>

        <Button type="submit" disabled={loading} data-testid="register-submit"
          className="w-full mt-6 bg-zinc-950 text-white hover:bg-zinc-800 rounded-sm">
          {loading ? "Creazione…" : "Crea account"}
        </Button>

        <p className="text-sm text-zinc-500 mt-6 text-center">
          Hai già un account?{" "}
          <Link to="/login" data-testid="goto-login" className="text-zinc-950 font-medium underline underline-offset-2">Accedi</Link>
        </p>
      </form>
    </div>
  );
}
